import React, { useCallback, useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import { useNavigate } from 'react-router-dom';
import { useUserRights } from '../../context/UserRightsContext';

const RIGHT_FIELDS = ['access', 'add', 'edit', 'view', 'execute'];

const sameId = (a, b) => String(a ?? '') === String(b ?? '');

const UserRightsDrawer = ({ open, onClose, roleId, roleName }) => {
  const navigate = useNavigate();
  const { ROLES_API_BASE, getRolesHeaders } = useUserRights();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userRights, setUserRights] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [tenantMenuScoped, setTenantMenuScoped] = useState(false);
  const [scopeMessage, setScopeMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [mergedData, setMergedData] = useState([]);
  const [expanded, setExpanded] = useState('userRights');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const tenantId = localStorage.getItem('tenantId') || localStorage.getItem('tenant_id');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchFunctions = useCallback(async () => {
    if (!tenantId) {
      setFunctions([]);
      setTenantMenuScoped(false);
      setScopeMessage('No tenant selected — cannot load scoped functions.');
      return;
    }
    try {
      const resp = await fetch(
        `${ROLES_API_BASE}/functions/scoped?tenantId=${encodeURIComponent(tenantId)}`,
        { headers: getRolesHeaders() }
      );
      if (!resp.ok) throw new Error('Failed to fetch scoped functions');
      const data = await resp.json();
      const list = Array.isArray(data?.functions) ? data.functions : Array.isArray(data) ? data : [];
      setFunctions(list);
      setTenantMenuScoped(Boolean(data?.tenantMenuScoped));
      setScopeMessage(
        data?.tenantMenuScoped
          ? `Showing ${list.length} function(s) for menus assigned to this tenant.`
          : data?.message ||
              'No tenant menu assignments yet — showing full function list. Assign menus under Tenants first.'
      );
    } catch (err) {
      console.error('Error fetching scoped functions:', err);
      setFunctions([]);
      setTenantMenuScoped(false);
      setScopeMessage(null);
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ROLES_API_BASE, tenantId]);

  const fetchUserRights = useCallback(async () => {
    if (!roleId || !tenantId) return;
    const resp = await fetch(
      `${ROLES_API_BASE}/rights/tenant/${tenantId}/role/${roleId}`,
      { headers: getRolesHeaders() }
    );
    if (!resp.ok) throw new Error('Failed to fetch user rights');
    const data = await resp.json();
    setUserRights(Array.isArray(data) ? data : []);
    setHasChanges(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ROLES_API_BASE, roleId, tenantId]);

  useEffect(() => {
    if (!open || !roleId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setPage(0);
      try {
        await Promise.all([fetchFunctions(), fetchUserRights()]);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load rights');
          setUserRights([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, roleId, tenantId, fetchFunctions, fetchUserRights]);

  useEffect(() => {
    const merged = functions.map((func) => {
      const fid = func.functionId ?? func.function_id;
      const menuCode = (func.menuCode ?? func.menu_code ?? '').toString().trim();
      const existingRight = userRights.find((r) =>
        sameId(r.functionId ?? r.function_id, fid)
      );
      if (existingRight) {
        return {
          id: String(existingRight.id ?? `right_${fid}`),
          functionId: fid,
          function: func.name,
          menuCode,
          access: Boolean(existingRight.access),
          add: Boolean(existingRight.add),
          edit: Boolean(existingRight.edit),
          view: Boolean(existingRight.view),
          execute: Boolean(existingRight.execute),
        };
      }
      return {
        id: `new_${fid}`,
        functionId: fid,
        function: func.name,
        menuCode,
        access: false,
        add: false,
        edit: false,
        view: false,
        execute: false,
      };
    });
    setMergedData(merged);
  }, [functions, userRights]);

  const handleRightChange = (itemId, field, checked) => {
    setMergedData((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const next = { ...item, [field]: checked };

        // Turning Access off clears CRUD flags
        if (field === 'access' && !checked) {
          next.add = false;
          next.edit = false;
          next.view = false;
          next.execute = false;
        }
        // Turning on Add/Edit/View/Execute implies Access
        if (field !== 'access' && checked) {
          next.access = true;
        }
        return next;
      })
    );
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    if (!tenantId || !roleId) return;
    setSaving(true);
    setError(null);
    try {
      const bulkUpdateData = mergedData.map((item) => ({
        tenantId: Number(tenantId),
        roleId,
        functionId: item.functionId,
        access: Boolean(item.access),
        add: Boolean(item.add),
        edit: Boolean(item.edit),
        view: Boolean(item.view),
        execute: Boolean(item.execute),
      }));
      const resp = await fetch(`${ROLES_API_BASE}/rights/bulk`, {
        method: 'POST',
        headers: getRolesHeaders(),
        body: JSON.stringify(bulkUpdateData),
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || errData.message || `Failed to save: ${resp.status}`);
      }
      setHasChanges(false);
      showSnackbar('User rights saved successfully.', 'success');
      await fetchUserRights();
    } catch (err) {
      console.error('Error saving user rights:', err);
      setError(err.message);
      showSnackbar(err.message || 'Failed to save user rights', 'error');
    } finally {
      setSaving(false);
    }
  };

  const pagedRows = mergedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderRightCheckbox = (row, field) => {
    const disabled = field !== 'access' && !row.access;
    return (
      <Checkbox
        size="small"
        checked={Boolean(row[field])}
        disabled={disabled || saving}
        onChange={(e) => handleRightChange(row.id, field, e.target.checked)}
        inputProps={{ 'aria-label': `${row.function} ${field}` }}
      />
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 680 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #eee', gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }} noWrap>
            User Rights {roleName ? `– ${roleName}` : ''}
          </Typography>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={saving || !hasChanges || mergedData.length === 0}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 1 }}>
          {scopeMessage && !loading && (
            <Alert severity={tenantMenuScoped ? 'info' : 'warning'} sx={{ py: 0.5 }}>
              {scopeMessage}{' '}
              {!tenantMenuScoped && (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    onClose();
                    navigate('/tenant');
                  }}
                >
                  Open Tenants
                </Link>
              )}
            </Alert>
          )}

          <Accordion
            expanded={expanded === 'userRights'}
            onChange={() => setExpanded(expanded === 'userRights' ? false : 'userRights')}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, '& .MuiAccordionDetails-root': { flex: 1 } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1, pr: 1 }}>
                <Typography sx={{ flex: 1 }}>Rights matrix</Typography>
                {hasChanges && (
                  <Typography variant="caption" color="warning.main">
                    Unsaved changes
                  </Typography>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveAll();
                  }}
                  disabled={saving || !hasChanges || mergedData.length === 0}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ height: 380, overflow: 'hidden', p: 0, display: 'flex', flexDirection: 'column' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>
              ) : mergedData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80, px: 2 }}>
                  <Typography color="text.secondary" align="center">
                    {tenantMenuScoped
                      ? 'No functions match assigned menus (by menu_code). Assign more menus under Tenants, or ensure Master Menus are seeded.'
                      : 'No functions defined.'}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Function</TableCell>
                          {RIGHT_FIELDS.map((f) => (
                            <TableCell key={f} align="center" sx={{ textTransform: 'capitalize', minWidth: 72 }}>
                              {f}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              <Typography variant="body2" component="span">
                                {row.function}
                              </Typography>
                              {row.menuCode ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ fontFamily: 'monospace', lineHeight: 1.2 }}
                                >
                                  {row.menuCode}
                                </Typography>
                              ) : null}
                            </TableCell>
                            {RIGHT_FIELDS.map((field) => (
                              <TableCell key={field} align="center" padding="checkbox">
                                {renderRightCheckbox(row, field)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={mergedData.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </AccordionDetails>
          </Accordion>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              pt: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={saving || !hasChanges || mergedData.length === 0}
            >
              {saving ? 'Saving...' : 'Save rights'}
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        message={snackbar.message}
      />
    </>
  );
};

export default UserRightsDrawer;
